import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { format } from "date-fns";
import { toast } from "sonner";
import { Calendar } from "@/components/ui/calendar";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Minus, Plus, Loader2 } from "lucide-react";
import { API, SERVICES, SERVICE_ORDER } from "@/lib/constants";

const inputCls = "bg-white border-[#1A243B]/15 rounded-xl h-12 text-base";
const labelCls = "text-sm font-bold text-[#1A243B]";

function Field({ label, required, children, testid }) {
  return (
    <div className="space-y-2" data-testid={testid}>
      <Label className={labelCls}>
        {label} {required && <span className="text-[#1A243B]">*</span>}
      </Label>
      {children}
    </div>
  );
}

function ChipGroup({ value, onChange, options, testid }) {
  return (
    <ToggleGroup
      type="single"
      value={value}
      onValueChange={(v) => v && onChange(v)}
      className="justify-start gap-2 flex-wrap"
      data-testid={testid}
    >
      {options.map((o) => (
        <ToggleGroupItem
          key={o}
          value={o}
          data-testid={`${testid}-${o.toLowerCase().replace(/\s+/g, "-")}`}
          className="rounded-full border border-[#1A243B]/20 bg-white px-5 data-[state=on]:bg-[#1A243B] data-[state=on]:text-[#FFC107] font-semibold"
        >
          {o}
        </ToggleGroupItem>
      ))}
    </ToggleGroup>
  );
}

export default function BookingSection() {
  const [date, setDate] = useState(undefined);
  const [service, setService] = useState("");
  const [step, setStep] = useState("pick"); // pick | form
  const [submitting, setSubmitting] = useState(false);
  const [availability, setAvailability] = useState({ blocked_dates: [], full_slots: {}, time_slots: [] });

  const [form, setForm] = useState({
    name: "", email: "", phone: "",
    student_name: "", grade: "", county: "", preferred_time: "", time_slot: "",
    num_students: 1, student_names: [""], location: "",
    meet: "", topic: "", subject: "", curriculum: "", hours: 1, notes: "",
  });

  useEffect(() => {
    if (typeof window !== "undefined" && window.location.hash.startsWith("#book?service=")) {
      const s = window.location.hash.split("service=")[1];
      if (SERVICES[s]) setService(s);
    }
    axios.get(`${API}/availability`).then((r) => setAvailability(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!service || !SERVICES[service]?.minStudents) return;
    setStudentCount(SERVICES[service].minStudents);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [service]);

  const isBlocked = (d) => availability.blocked_dates.includes(format(d, "yyyy-MM-dd"));
  const dateKey = date ? format(date, "yyyy-MM-dd") : "";
  const serviceSlots = service ? (availability.service_slots?.[service]?.time_slots || []) : [];
  const fullToday = (availability.full_slots?.[dateKey] || {})[service] || [];

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const setStudentCount = (n) => {
    const cfg = SERVICES[service] || {};
    const min = cfg.minStudents || 1;
    const max = cfg.maxStudents || 30;
    const count = Math.min(max, Math.max(min, n));
    setForm((f) => ({
      ...f,
      num_students: count,
      student_names: Array.from({ length: count }, (_, i) => f.student_names[i] || ""),
    }));
  };

  const total = useMemo(() => {
    if (!service) return 0;
    const s = SERVICES[service];
    if (service === "group" || service === "individual") return s.price * form.num_students;
    if (service === "tutoring") return s.price * form.hours * 1;
    return s.price;
  }, [service, form.num_students, form.hours]);

  const canContinue = date && service && form.time_slot;

  const validate = () => {
    if (!form.name.trim()) return "Please add your name.";
    if (!form.email.trim() || !/^\S+@\S+\.\S+$/.test(form.email)) return "Please add a valid email address.";
    if ((service === "individual" || service === "group") && form.student_names.some((n) => !n.trim())) return "Please add every student's name.";
    if (service === "individual" && !form.grade.trim()) return "Please add the grade(s) completed this year.";
    if (service === "tutoring" && !form.student_names[0]?.trim()) return "Please add the student name(s).";
    return null;
  };

  const submit = async () => {
    const err = validate();
    if (err) { toast.error(err); return; }
    setSubmitting(true);
    try {
      const payload = {
        service,
        date: format(date, "yyyy-MM-dd"),
        name: form.name,
        email: form.email,
        phone: form.phone,
        origin_url: window.location.origin,
        num_students: (service === "group" || service === "individual") ? form.num_students : 1,
        hours: service === "tutoring" ? form.hours : 1,
        details: {
          student_name: form.student_names[0] || "",
          grade: form.grade,
          county: form.county,
          preferred_time: form.preferred_time,
          time_slot: form.time_slot,
          student_names: service === "tutoring"
            ? form.student_names[0].split(",").map((s) => s.trim()).filter(Boolean)
            : form.student_names.filter(Boolean),
          location: form.location,
          meet: form.meet,
          topic: form.topic,
          subject: form.subject,
          curriculum: form.curriculum,
          notes: form.notes,
        },
      };
      const res = await axios.post(`${API}/bookings/checkout`, payload);
      window.location.href = res.data.checkout_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Something went wrong starting checkout. Please try again.");
      setSubmitting(false);
    }
  };

  const dateLabel = date ? format(date, "EEE, MMMM d, yyyy") : "";

  return (
    <section id="book" data-testid="booking-section" className="bg-[#EBEBFA] border-t border-[#1A243B]/10">
      <div className="max-w-6xl mx-auto md:grid md:grid-cols-2">
        <div className="px-6 md:px-10 py-16 md:py-24">
          <h2 className="text-3xl md:text-4xl text-[#1A243B] mb-4">Book <span className="italic">online</span></h2>
          <p className="text-base md:text-lg text-[#2D3748] mb-10">
            Pick a date, then choose what you'd like to book.
          </p>

          <div className="bg-white rounded-3xl p-6 md:p-8 shadow-sm w-fit" data-testid="booking-calendar-wrap">
            <Calendar
              mode="single"
              selected={date}
              onSelect={(d) => { setDate(d); set("time_slot", ""); }}
              disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0)) || isBlocked(d)}
              data-testid="booking-calendar"
              className="rounded-xl"
            />
          </div>

          {date && (
            <p className="mt-8 font-display text-xl text-[#1A243B]" data-testid="booking-date-label">
              {format(date, "EEEE, MMMM d, yyyy")}
            </p>
          )}

          <div className="mt-6">
            <p className="text-sm font-bold text-[#1A243B] mb-3">What would you like to book?</p>
            <Select
              value={service}
              onValueChange={(v) => { setService(v); set("time_slot", ""); }}
            >
              <SelectTrigger data-testid="service-select" className="bg-white border-[#1A243B]/15 rounded-xl h-12 max-w-md text-base">
                <SelectValue placeholder="Select a service" />
              </SelectTrigger>
              <SelectContent>
                {SERVICE_ORDER.map((k) => (
                  <SelectItem key={k} value={k} data-testid={`service-option-${k}`}>
                    {SERVICES[k].label} — ${SERVICES[k].price}{SERVICES[k].unit}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {date && service && (
            <div className="mt-8">
              <p className="text-sm font-bold text-[#1A243B] mb-3">Pick a time</p>
              <div className="flex flex-wrap gap-2 max-w-md" data-testid="time-slot-picker">
                {serviceSlots.map((slot) => {
                  const full = fullToday.includes(slot);
                  return (
                    <button
                      key={slot}
                      type="button"
                      data-testid={`time-slot-${slot.replace(/[\s:]/g, "-").toLowerCase()}`}
                      disabled={full}
                      onClick={() => set("time_slot", slot)}
                      className={`px-5 py-2.5 rounded-full border font-semibold text-sm transition-colors ${
                        full
                          ? "bg-[#1A243B]/5 text-[#1A243B]/35 border-transparent line-through cursor-not-allowed"
                          : form.time_slot === slot
                            ? "bg-[#1A243B] text-[#FFC107] border-[#1A243B]"
                            : "bg-white text-[#1A243B] border-[#1A243B]/20 hover:border-[#1A243B]/50"
                      }`}
                    >
                      {slot}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          <button
            data-testid="booking-continue-btn"
            disabled={!canContinue}
            onClick={() => setStep("form")}
            className="cta-pill mt-8 bg-[#FFC107] text-[#1A243B] font-bold px-10 py-3.5 rounded-full disabled:opacity-40 disabled:pointer-events-none"
          >
            Continue
          </button>
        </div>
        <div className="relative min-h-[300px] md:min-h-0 hidden md:block">
          <img
            src="/images/boy-worksheet.png"
            alt="A young student tracing letters on a worksheet"
            data-testid="booking-image"
            className="torn-right absolute inset-0 w-full h-full object-cover"
          />
        </div>
      </div>

      {step === "form" && service && (
        <div className="border-t border-[#1A243B]/10">
          <div className="max-w-6xl mx-auto px-6 md:px-10 py-14 md:py-20 grid lg:grid-cols-3 gap-10">
            <div className="lg:col-span-2 bg-white rounded-3xl p-8 md:p-10 shadow-sm" data-testid="booking-form">
              <h3 className="text-2xl md:text-3xl text-[#1A243B] mb-2">{SERVICES[service].formTitle}</h3>
              <p className="text-[#2D3748] text-base mb-8">{SERVICES[service].formSub}</p>

              <div className="grid sm:grid-cols-2 gap-5">
                {service === "group" ? (
                  <Field label="Organizer name" required testid="field-name">
                    <Input data-testid="input-name" className={inputCls} value={form.name} onChange={(e) => set("name", e.target.value)} />
                  </Field>
                ) : service === "consultation" ? (
                  <Field label="Your name" required testid="field-name">
                    <Input data-testid="input-name" className={inputCls} value={form.name} onChange={(e) => set("name", e.target.value)} />
                  </Field>
                ) : (
                  <Field label="Parent / guardian name" required testid="field-name">
                    <Input data-testid="input-name" className={inputCls} value={form.name} onChange={(e) => set("name", e.target.value)} />
                  </Field>
                )}

                <Field label="Email address" required testid="field-email">
                  <Input data-testid="input-email" type="email" className={inputCls} value={form.email} onChange={(e) => set("email", e.target.value)} />
                </Field>

                <Field label={service === "individual" ? "Phone number" : "Phone number"} required={service === "individual"} testid="field-phone">
                  <Input data-testid="input-phone" className={inputCls} value={form.phone} onChange={(e) => set("phone", e.target.value)} />
                </Field>

                {service === "individual" && (
                  <>
                    <Field label="Number of students" required testid="field-num-students">
                      <div className="flex items-center gap-3">
                        <button type="button" data-testid="students-minus" onClick={() => setStudentCount(form.num_students - 1)} className="w-11 h-11 rounded-full border border-[#1A243B]/20 bg-white flex items-center justify-center hover:bg-[#EBEBFA] transition-colors">
                          <Minus className="w-4 h-4" />
                        </button>
                        <span data-testid="students-count" className="font-bold text-lg w-8 text-center">{form.num_students}</span>
                        <button type="button" data-testid="students-plus" onClick={() => setStudentCount(form.num_students + 1)} className="w-11 h-11 rounded-full border border-[#1A243B]/20 bg-white flex items-center justify-center hover:bg-[#EBEBFA] transition-colors">
                          <Plus className="w-4 h-4" />
                        </button>
                      </div>
                    </Field>
                    <Field label="Grade(s) completed this year" required testid="field-grade">
                      <Input data-testid="input-grade" placeholder="e.g. 3rd" className={inputCls} value={form.grade} onChange={(e) => set("grade", e.target.value)} />
                    </Field>
                    <div className="sm:col-span-2 space-y-3">
                      <Label className={labelCls}>Name(s) of student(s) *</Label>
                      {form.student_names.map((n, i) => (
                        <Input
                          key={i}
                          data-testid={i === 0 ? "input-student-name" : `input-student-${i + 1}`}
                          placeholder={form.num_students === 1 ? "Student — full name" : `Student ${i + 1} — full name`}
                          className={inputCls}
                          value={n}
                          onChange={(e) => {
                            const names = [...form.student_names];
                            names[i] = e.target.value;
                            set("student_names", names);
                          }}
                        />
                      ))}
                    </div>
                    <Field label="County" testid="field-county">
                      <Input data-testid="input-county" className={inputCls} value={form.county} onChange={(e) => set("county", e.target.value)} />
                    </Field>
                    <div className="sm:col-span-2">
                      <Field label="Preferred time" testid="field-preferred-time">
                        <ChipGroup testid="preferred-time" value={form.preferred_time} onChange={(v) => set("preferred_time", v)} options={["Morning", "Afternoon"]} />
                      </Field>
                    </div>
                    <div className="sm:col-span-2">
                      <Field label="Accommodations or anything we should know" testid="field-notes">
                        <Textarea data-testid="input-notes" className="bg-white border-[#1A243B]/15 rounded-xl text-base" value={form.notes} onChange={(e) => set("notes", e.target.value)} />
                      </Field>
                    </div>
                  </>
                )}

                {service === "group" && (
                  <>
                    <Field label="Number of students" required testid="field-num-students">
                      <div className="flex items-center gap-3">
                        <button type="button" data-testid="students-minus" onClick={() => setStudentCount(form.num_students - 1)} className="w-11 h-11 rounded-full border border-[#1A243B]/20 bg-white flex items-center justify-center hover:bg-[#EBEBFA] transition-colors">
                          <Minus className="w-4 h-4" />
                        </button>
                        <span data-testid="students-count" className="font-bold text-lg w-8 text-center">{form.num_students}</span>
                        <button type="button" data-testid="students-plus" onClick={() => setStudentCount(form.num_students + 1)} className="w-11 h-11 rounded-full border border-[#1A243B]/20 bg-white flex items-center justify-center hover:bg-[#EBEBFA] transition-colors">
                          <Plus className="w-4 h-4" />
                        </button>
                      </div>
                    </Field>
                    <Field label="Testing location (co-op, church, home)" testid="field-location">
                      <Input data-testid="input-location" className={inputCls} value={form.location} onChange={(e) => set("location", e.target.value)} />
                    </Field>
                    <div className="sm:col-span-2 space-y-3">
                      <Label className={labelCls}>Names of students *</Label>
                      {form.student_names.map((n, i) => (
                        <Input
                          key={i}
                          data-testid={`input-student-${i + 1}`}
                          placeholder={`Student ${i + 1} — full name`}
                          className={inputCls}
                          value={n}
                          onChange={(e) => {
                            const names = [...form.student_names];
                            names[i] = e.target.value;
                            set("student_names", names);
                          }}
                        />
                      ))}
                      <p className="text-sm text-[#2D3748]/70">One name field appears for each student.</p>
                    </div>
                  </>
                )}

                {service === "consultation" && (
                  <>
                    <div>
                      <Field label="Preferred time" testid="field-preferred-time">
                        <ChipGroup testid="preferred-time" value={form.preferred_time} onChange={(v) => set("preferred_time", v)} options={["Morning", "Afternoon", "Evening"]} />
                      </Field>
                    </div>
                    <div>
                      <Field label="Meet" testid="field-meet">
                        <ChipGroup testid="meet" value={form.meet} onChange={(v) => set("meet", v)} options={["In person", "Online"]} />
                      </Field>
                    </div>
                    <div className="sm:col-span-2">
                      <Field label="What would you like to discuss?" testid="field-topic">
                        <Textarea data-testid="input-topic" className="bg-white border-[#1A243B]/15 rounded-xl text-base" value={form.topic} onChange={(e) => set("topic", e.target.value)} />
                      </Field>
                    </div>
                  </>
                )}

                {service === "tutoring" && (
                  <>
                    <Field label="Student name(s)" required testid="field-student-names">
                      <Input data-testid="input-student-names" className={inputCls} value={form.student_names[0]} onChange={(e) => set("student_names", [e.target.value])} />
                    </Field>
                    <Field label="Session length" required testid="field-hours">
                      <ChipGroup
                        testid="session-length"
                        value={String(form.hours)}
                        onChange={(v) => set("hours", parseFloat(v))}
                        options={["1", "1.5"].map(String)}
                      />
                    </Field>
                    <div className="sm:col-span-2">
                      <Field label="Subject" testid="field-subject">
                        <ChipGroup testid="subject" value={form.subject} onChange={(v) => set("subject", v)} options={["Reading", "Writing", "Spelling", "Math"]} />
                      </Field>
                    </div>
                    <div className="sm:col-span-2">
                      <Field label="Curriculum you use (optional)" testid="field-curriculum">
                        <Input data-testid="input-curriculum" className={inputCls} value={form.curriculum} onChange={(e) => set("curriculum", e.target.value)} />
                      </Field>
                    </div>
                  </>
                )}
              </div>
            </div>

            <div>
              <div className="bg-[#1A243B] text-[#EBEBFA] rounded-3xl p-8 shadow-sm sticky top-24" data-testid="booking-summary">
                <h4 className="font-display text-xl mb-6">Your booking</h4>
                <dl className="space-y-3 text-sm">
                  <div className="flex justify-between gap-4">
                    <dt className="text-[#EBEBFA]/70">Date</dt>
                    <dd className="font-bold text-right" data-testid="summary-date">{dateLabel}</dd>
                  </div>
                  <div className="flex justify-between gap-4">
                    <dt className="text-[#EBEBFA]/70">Time</dt>
                    <dd className="font-bold text-right" data-testid="summary-time">{form.time_slot}</dd>
                  </div>
                  <div className="flex justify-between gap-4">
                    <dt className="text-[#EBEBFA]/70">Service</dt>
                    <dd className="font-bold text-right" data-testid="summary-service">
                      {SERVICES[service].name} ${SERVICES[service].price}.00
                      {(service === "group" || (service === "individual" && form.num_students > 1)) && ` x ${form.num_students}`}
                      {service === "tutoring" && form.hours > 1 && ` x ${form.hours} hrs`}
                    </dd>
                  </div>
                  {(service === "group" || (service === "individual" && form.num_students > 1)) && (
                    <div className="flex justify-between gap-4">
                      <dt className="text-[#EBEBFA]/70">Students</dt>
                      <dd className="font-bold" data-testid="summary-students">{form.num_students}</dd>
                    </div>
                  )}
                  {service === "tutoring" && (
                    <div className="flex justify-between gap-4">
                      <dt className="text-[#EBEBFA]/70">Session length</dt>
                      <dd className="font-bold" data-testid="summary-hours">{form.hours} hour{form.hours > 1 ? "s" : ""}</dd>
                    </div>
                  )}
                  {service === "consultation" && (
                    <div className="flex justify-between gap-4">
                      <dt className="text-[#EBEBFA]/70">Length</dt>
                      <dd className="font-bold">1 hour</dd>
                    </div>
                  )}
                  <div className="border-t border-[#EBEBFA]/20 pt-4 flex justify-between items-baseline">
                    <dt className="font-display text-lg">Total</dt>
                    <dd className="font-display text-2xl font-bold text-[#FFC107]" data-testid="summary-total">${total}</dd>
                  </div>
                </dl>
                <button
                  data-testid="pay-confirm-btn"
                  onClick={submit}
                  disabled={submitting}
                  className="cta-pill mt-8 w-full bg-[#FFC107] text-[#1A243B] font-bold py-3.5 rounded-full flex items-center justify-center gap-2 disabled:opacity-60"
                >
                  {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
                  Pay &amp; confirm
                </button>
                <p className="text-xs text-[#EBEBFA]/60 mt-4 leading-relaxed" data-testid="summary-travel-note">
                  Outside the listed service areas? Julie is happy to travel to you; a travel fee may apply — she'll confirm it with you before your session.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
